import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
    Percent,
    CalendarClock,
    Layers,
    HeartPulse,
    Siren,
    ShieldAlert,
    CheckCircle2,
    AlertTriangle,
    RotateCcw,
    Shield,
    OctagonX,
    Ban
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

// Risk Rules Checklist Data Structure
const riskRulesChecklist = {
    risiko_per_trade: {
        title: 'Risiko Per Trade',
        title_en: 'Risk Per Trade',
        icon: Percent,
        color: 'blue',
        items: [
            { id: 'rpt_1', text: 'Risk per trade ikut plan (tidak melebihi had)', text_en: 'Risk per trade follows plan (does not exceed limit)' },
            { id: 'rpt_2', text: 'Saiz lot dikira berdasarkan SL', text_en: 'Lot size calculated based on SL' },
            { id: 'rpt_3', text: 'Stop Loss ditetapkan sebelum entry', text_en: 'Stop Loss set before entry' }
        ]
    },
    risiko_harian: {
        title: 'Risiko Harian',
        title_en: 'Daily Risk',
        icon: CalendarClock,
        color: 'amber',
        items: [
            { id: 'rh_1', text: 'Had loss harian jelas', text_en: 'Daily loss limit is clear' },
            { id: 'rh_2', text: 'Jumlah maksimum SL harian diketahui', text_en: 'Maximum daily SL count known' },
            { id: 'rh_3', text: 'Jika had tercapai → STOP TRADE', text_en: 'If limit reached → STOP TRADE', isStop: true }
        ]
    },
    exposure_control: {
        title: 'Exposure & Control',
        title_en: 'Exposure & Control',
        icon: Layers,
        color: 'purple',
        items: [
            { id: 'ec_1', text: 'Tidak buka trade bertindih tanpa sebab', text_en: 'No overlapping trades without reason' },
            { id: 'ec_2', text: 'Tidak tambah posisi (no revenge / no averaging)', text_en: 'No adding positions (no revenge / no averaging)' },
            { id: 'ec_3', text: 'Tidak all-in walaupun setup kelihatan cantik', text_en: 'No all-in even if setup looks perfect' }
        ]
    },
    psikologi_risiko: {
        title: 'Psikologi Risiko',
        title_en: 'Risk Psychology',
        icon: HeartPulse,
        color: 'emerald',
        items: [
            { id: 'pr_1', text: 'Redha dengan risiko sebelum entry', text_en: 'Accept the risk before entry' },
            { id: 'pr_2', text: 'Sedia terima SL tanpa emosi', text_en: 'Ready to accept SL without emotion' },
            { id: 'pr_3', text: 'Tidak cuba balas market', text_en: 'Not trying to fight the market' }
        ]
    },
    emergency_rules: {
        title: 'Emergency Rules',
        title_en: 'Emergency Rules',
        icon: Siren,
        color: 'rose',
        items: [
            { id: 'er_1', text: 'Emosi terganggu → STOP TRADE', text_en: 'Emotions disturbed → STOP TRADE', isStop: true },
            { id: 'er_2', text: 'Hilang fokus → STOP TRADE', text_en: 'Lost focus → STOP TRADE', isStop: true },
            { id: 'er_3', text: 'Langgar satu rule → STOP SESI', text_en: 'Violate one rule → STOP SESSION', isStop: true }
        ]
    },
    final_confirmation: {
        title: 'Final Confirmation',
        title_en: 'Final Confirmation',
        icon: ShieldAlert,
        color: 'primary',
        items: [
            { id: 'fc_1', text: 'Risiko difahami sepenuhnya', text_en: 'Risk fully understood' },
            { id: 'fc_2', text: 'Trade tidak menjejaskan survival account', text_en: 'Trade does not affect account survival' },
            { id: 'fc_3', text: 'Jika ragu → NO TRADE', text_en: 'If in doubt → NO TRADE', isStop: true }
        ]
    }
};

const colorStyles = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', ring: 'ring-blue-500/20' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', ring: 'ring-amber-500/20' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', ring: 'ring-purple-500/20' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', ring: 'ring-emerald-500/20' },
    rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', ring: 'ring-rose-500/20' },
    primary: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', ring: 'ring-red-500/20' }
};

// Get all STOP items
const getStopItems = () => {
    const stopItems = [];
    Object.values(riskRulesChecklist).forEach(section => {
        section.items.forEach(item => {
            if (item.isStop) stopItems.push(item.id);
        });
    });
    return stopItems;
};

export const RiskRulesChecklist = ({ onComplete, className }) => {
    const { language } = useLanguage();
    
    // Initialize checkedItems from localStorage using lazy initialization
    const [checkedItems, setCheckedItems] = useState(() => {
        const saved = localStorage.getItem('riskRulesChecklist');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const today = new Date().toDateString();
                if (parsed.date === today && parsed.items) {
                    return parsed.items;
                }
            } catch (e) {
                console.error('Error loading checklist:', e);
            }
        }
        return {};
    });
    const [showReminder, setShowReminder] = useState(false);
    const [ruleViolated, setRuleViolated] = useState(false);

    // Calculate total items and checked count
    const totalItems = Object.values(riskRulesChecklist).reduce((acc, section) => acc + section.items.length, 0);
    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    const isComplete = checkedCount === totalItems;
    const progress = Math.round((checkedCount / totalItems) * 100);

    // Ref to track if component has mounted
    const hasMounted = useRef(false);

    // Save to localStorage on change (skip initial mount)
    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        
        localStorage.setItem('riskRulesChecklist', JSON.stringify({
            date: new Date().toDateString(),
            items: checkedItems
        }));
    }, [checkedItems]);

    // Check if any STOP item is checked (means rule condition triggered)
    const stopItems = getStopItems();
    const stopTriggered = stopItems.some(id => checkedItems[id]);

    const handleToggle = (itemId) => {
        setCheckedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
        setShowReminder(false);
        setRuleViolated(false);
    };

    const handleReset = () => {
        setCheckedItems({});
        setShowReminder(false);
        setRuleViolated(false);
    };

    const handleViolation = () => {
        setRuleViolated(true);
    };

    const handleProceed = () => {
        if (!isComplete) {
            setShowReminder(true);
            return;
        }
        if (onComplete) {
            onComplete();
        }
    };

    // Calculate section completion
    const getSectionProgress = (sectionKey) => {
        const section = riskRulesChecklist[sectionKey];
        const sectionChecked = section.items.filter(item => checkedItems[item.id]).length;
        return {
            checked: sectionChecked,
            total: section.items.length,
            complete: sectionChecked === section.items.length
        };
    };

    return (
        <div className={cn("w-full max-w-3xl mx-auto", className)} data-testid="risk-rules-checklist">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="text-sm font-semibold text-primary">
                        {language === 'id' ? 'Wajib Sebelum Trade' : 'Required Before Trading'}
                    </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                    Risk Rules Checklist
                </h2>
                <p className="text-slate-500 mt-2">
                    {language === 'id' 
                        ? 'Lindungi modal anda. Ikut peraturan tanpa kompromi.'
                        : 'Protect your capital. Follow the rules without compromise.'
                    }
                </p>
                
                {/* Progress Bar */}
                <div className="mt-6 flex items-center justify-center gap-4">
                    <div className="flex-1 max-w-xs h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                            className={cn(
                                "h-full rounded-full transition-all duration-500 ease-out",
                                isComplete ? "bg-emerald-500" : "bg-primary"
                            )}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className={cn(
                        "text-sm font-semibold",
                        isComplete ? "text-emerald-600" : "text-slate-600"
                    )}>
                        {checkedCount}/{totalItems}
                    </span>
                </div>
            </div>

            {/* Rule Violation Warning Banner */}
            {ruleViolated && (
                <div 
                    className="mb-6 flex items-start gap-3 p-4 bg-red-100 border-2 border-red-400 rounded-xl animate-in fade-in"
                    data-testid="rule-violation-warning"
                >
                    <OctagonX className="w-6 h-6 text-red-700 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold text-red-900">
                            {language === 'id' 
                                ? 'Peraturan risiko dilanggar. Sesi trade dihentikan.'
                                : 'Risk rule violated. Trading session stopped.'
                            }
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                            {language === 'id'
                                ? 'Ambil masa untuk berehat. Kembali esok dengan disiplin baru.'
                                : 'Take time to rest. Return tomorrow with renewed discipline.'
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* STOP Trade Warning Banner */}
            {stopTriggered && !ruleViolated && (
                <div 
                    className="mb-6 flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-300 rounded-xl animate-in fade-in"
                    data-testid="stop-trade-warning"
                >
                    <Ban className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold text-amber-900">
                            {language === 'id' 
                                ? 'STOP condition aktif. Pastikan anda memahami bila perlu berhenti.'
                                : 'STOP condition active. Make sure you understand when to stop.'
                            }
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                            {language === 'id'
                                ? 'Menanda item STOP bermaksud anda tahu bila harus berhenti trading.'
                                : 'Checking STOP items means you know when to stop trading.'
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Checklist Sections */}
            <div className="space-y-4">
                {Object.entries(riskRulesChecklist).map(([sectionKey, section]) => {
                    const Icon = section.icon;
                    const colors = colorStyles[section.color];
                    const sectionProgress = getSectionProgress(sectionKey);
                    const isEmergencySection = sectionKey === 'emergency_rules';

                    return (
                        <div 
                            key={sectionKey}
                            className={cn(
                                "bg-white rounded-2xl border overflow-hidden transition-all duration-300",
                                sectionProgress.complete 
                                    ? isEmergencySection
                                        ? "border-rose-200 shadow-sm shadow-rose-100"
                                        : "border-emerald-200 shadow-sm shadow-emerald-100" 
                                    : "border-slate-100"
                            )}
                            data-testid={`section-${sectionKey}`}
                        >
                            {/* Section Header */}
                            <div className={cn(
                                "flex items-center gap-3 px-5 py-4 border-b transition-colors",
                                sectionProgress.complete 
                                    ? isEmergencySection
                                        ? "bg-rose-50/50 border-rose-100"
                                        : "bg-emerald-50/50 border-emerald-100" 
                                    : "bg-slate-50/50 border-slate-100"
                            )}>
                                <div className={cn(
                                    "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                                    sectionProgress.complete
                                        ? isEmergencySection
                                            ? "bg-rose-500 text-white"
                                            : "bg-emerald-500 text-white"
                                        : `${colors.bg} ${colors.text}`
                                )}>
                                    {sectionProgress.complete 
                                        ? isEmergencySection 
                                            ? <Siren className="w-5 h-5" />
                                            : <CheckCircle2 className="w-5 h-5" />
                                        : <Icon className="w-5 h-5" />
                                    }
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-slate-900">
                                        {language === 'en' ? section.title_en : section.title}
                                    </h3>
                                    <p className="text-xs text-slate-400">
                                        {sectionProgress.checked}/{sectionProgress.total} {language === 'id' ? 'selesai' : 'complete'}
                                    </p>
                                </div>
                                {sectionProgress.complete && (
                                    <span className={cn(
                                        "text-xs font-medium px-2 py-1 rounded-full",
                                        isEmergencySection 
                                            ? "text-rose-600 bg-rose-100"
                                            : "text-emerald-600 bg-emerald-100"
                                    )}>
                                        ✓
                                    </span>
                                )}
                            </div>

                            {/* Section Items */}
                            <div className="divide-y divide-slate-50">
                                {section.items.map((item) => {
                                    const isChecked = checkedItems[item.id] || false;
                                    const isStopItem = item.isStop;

                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => handleToggle(item.id)}
                                            className={cn(
                                                "w-full flex items-center gap-4 px-5 py-4 text-left transition-all duration-200 hover:bg-slate-50/50",
                                                isChecked && (isStopItem ? "bg-amber-50/30" : "bg-emerald-50/30")
                                            )}
                                            data-testid={`item-${item.id}`}
                                        >
                                            {/* Checkbox */}
                                            <div className={cn(
                                                "w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200",
                                                isChecked
                                                    ? isStopItem
                                                        ? "bg-amber-500 border-amber-500 text-white"
                                                        : "bg-emerald-500 border-emerald-500 text-white"
                                                    : isStopItem
                                                        ? "border-amber-300 hover:border-amber-400"
                                                        : "border-slate-200 hover:border-slate-300"
                                            )}>
                                                {isChecked && (
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </div>

                                            {/* Item Text */}
                                            <span className={cn(
                                                "flex-1 text-sm md:text-base transition-colors",
                                                isChecked 
                                                    ? "text-slate-400 line-through" 
                                                    : isStopItem
                                                        ? "text-amber-700 font-medium"
                                                        : "text-slate-700"
                                            )}>
                                                {language === 'en' ? item.text_en : item.text}
                                            </span>

                                            {/* STOP indicator */}
                                            {isStopItem && !isChecked && (
                                                <span className="text-[10px] text-amber-600 font-bold bg-amber-100 px-2 py-1 rounded-full">
                                                    STOP
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Reminder Message */}
            {showReminder && !isComplete && (
                <div 
                    className="mt-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-in fade-in slide-in-from-top-2"
                    data-testid="reminder-message"
                >
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium text-amber-800">
                            {language === 'id' 
                                ? 'Risk rules checklist belum lengkap.'
                                : 'Risk rules checklist is not complete.'
                            }
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                            {language === 'id'
                                ? 'Lengkapkan semua item untuk memastikan modal anda terlindung.'
                                : 'Complete all items to ensure your capital is protected.'
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {isComplete && !ruleViolated && (
                <div 
                    className="mt-6 flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl animate-in fade-in slide-in-from-top-2"
                    data-testid="success-message"
                >
                    <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-medium text-emerald-800">
                            {language === 'id'
                                ? 'Risk rules checklist lengkap!'
                                : 'Risk rules checklist complete!'
                            }
                        </p>
                        <p className="text-sm text-emerald-700 mt-1">
                            {language === 'id'
                                ? 'Modal anda terlindung. Trade dengan disiplin.'
                                : 'Your capital is protected. Trade with discipline.'
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                    variant="outline"
                    onClick={handleReset}
                    className="w-full sm:w-auto rounded-xl px-6"
                    data-testid="reset-btn"
                >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {language === 'id' ? 'Reset Checklist' : 'Reset Checklist'}
                </Button>

                <Button
                    variant="outline"
                    onClick={handleViolation}
                    className="w-full sm:w-auto rounded-xl px-6 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="violation-btn"
                >
                    <OctagonX className="w-4 h-4 mr-2" />
                    {language === 'id' ? 'Laporkan Pelanggaran' : 'Report Violation'}
                </Button>
                
                <Button
                    onClick={handleProceed}
                    disabled={ruleViolated}
                    className={cn(
                        "w-full sm:w-auto rounded-xl px-8",
                        ruleViolated
                            ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed"
                            : isComplete 
                                ? "bg-emerald-600 hover:bg-emerald-700" 
                                : "bg-primary hover:bg-primary/90"
                    )}
                    data-testid="proceed-btn"
                >
                    {ruleViolated ? (
                        <>
                            <Ban className="w-4 h-4 mr-2" />
                            {language === 'id' ? 'Sesi Dihentikan' : 'Session Stopped'}
                        </>
                    ) : isComplete ? (
                        <>
                            <Shield className="w-4 h-4 mr-2" />
                            {language === 'id' ? 'Sedia Trade' : 'Ready to Trade'}
                        </>
                    ) : (
                        language === 'id' ? 'Semak Rules' : 'Check Rules'
                    )}
                </Button>
            </div>

            {/* Wisdom Quote */}
            <div className="mt-8 text-center">
                <div className="inline-block px-6 py-3 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-600 font-medium">
                        &ldquo;{language === 'id' 
                            ? 'Protect capital first. Profit comes later.'
                            : 'Protect capital first. Profit comes later.'
                        }&rdquo;
                    </p>
                </div>
            </div>
        </div>
    );
};
